import type { Messages } from './types';

export const en: Messages = {
  'app.tagline': 'Five prayers, every day, kept together.',
  'app.blurb':
    'Keep a streak for praying, praying on time, praying in jamā‘ah, and catching the first takbīrah.',
  'app.streakLabel': 'Salāh streak',
  'app.streakOwner': '{{name}}’s streak',
  'app.provisionalName': 'Working name — not final',

  'welcome.start.kids': 'Bismillāh — let’s start',
  'welcome.start.adult': 'Get started',
  'welcome.privacy': 'Everything stays on this phone. Nothing is sent anywhere.',

  'onboarding.title': 'Who is praying?',
  'onboarding.nameLabel': 'Name',
  'onboarding.namePlaceholder': 'Your name',
  'onboarding.ageLabel': 'How old are you?',
  'onboarding.years': { one: '{{count}} year old', other: '{{count}} years old' },
  'onboarding.yearsUnit': { one: 'year old', other: 'years old' },
  'onboarding.agePlus': '{{age}}+',
  'onboarding.yearsPlus': '{{age}}+ years old',
  'onboarding.younger': 'Younger',
  'onboarding.older': 'Older',
  'onboarding.continue': 'Continue',
  'onboarding.privacy': 'This stays on the phone. A nickname is fine.',
  'onboarding.verdict.kids': '{{who}} gets the fun version',
  'onboarding.verdict.adult': '{{who}} gets the plain version',
  'onboarding.verdictNote.kids': 'lanterns, colours and rewards — {{age}} and under',
  'onboarding.verdictNote.adult': 'the same record, no game layer — {{age}} and over',

  'home.heroEyebrow': 'Prayers in a row',
  'home.prayersUnit': { one: 'prayer', other: 'prayers' },
  'home.days': { one: 'day', other: 'days' },
  'home.recorded': '{{count}} of {{total}} prayers recorded today',
  'home.today': 'Today',
  'home.now': 'Now',
  'home.markLink': 'Today’s prayer times — record one',
  'home.reward.title': {
    one: '{{quality}}, {{count}} prayer running',
    other: '{{quality}}, {{count}} prayers running',
  },
  'home.reward.sub': 'your lantern is at its brightest yet',
  'home.reward.litTitle': 'Your lantern is lit',
  'home.reward.litSub': 'all five today, and the run begins',
  'home.reward.emptyTitle': 'Your lantern is waiting',
  'home.reward.emptySub': 'record a prayer and it lights up',
  'home.hadith':
    'The deeds most beloved to Allāh are those done consistently, even if they are small.',
  'home.hadithSource': '— reported in Ṣaḥīḥ al-Bukhārī and Ṣaḥīḥ Muslim',

  'prayer.fajr': 'Fajr',
  'prayer.dhuhr': 'Dhuhr',
  'prayer.asr': '‘Asr',
  'prayer.maghrib': 'Maghrib',
  'prayer.isha': '‘Ishā’',
  'prayer.ar.fajr': 'الفجر',
  'prayer.ar.dhuhr': 'الظهر',
  'prayer.ar.asr': 'العصر',
  'prayer.ar.maghrib': 'المغرب',
  'prayer.ar.isha': 'العشاء',

  'note.later': 'later today',
  'note.missed': 'not recorded — you can make it up',
  'note.takbirah': 'first takbīrah with the imām',
  'note.jamaahOnTime': 'on time, in jamā‘ah',
  'note.jamaahLate': 'in jamā‘ah, after its time',
  'note.onTime': 'prayed in its time',
  'note.late': 'prayed, after its time',

  'quality.prayed.label': 'I prayed it',
  'quality.prayed.short': 'Prayed',
  'quality.prayed.kids': 'the first and biggest step',
  'quality.prayed.adult': 'the prayer was performed',
  'quality.onTime.label': 'In its time',
  'quality.onTime.short': 'On time',
  'quality.onTime.kids': 'before the next adhān called',
  'quality.onTime.adult': 'within the prescribed window',
  'quality.jamaah.label': 'In the masjid, in jamā‘ah',
  'quality.jamaah.short': 'Jamā‘ah',
  'quality.jamaah.kids': 'shoulder to shoulder in the rows',
  'quality.jamaah.adult': 'in congregation at the masjid',
  'quality.takbirah.label': 'Caught the first takbīrah',
  'quality.takbirah.short': '1st takbīrah',
  'quality.takbirah.kids': 'in the row before the imām began',
  'quality.takbirah.adult': 'present for the opening takbīr',
  'quality.needsJamaah': 'needs jamā‘ah first',
  'quality.ar.prayed': 'صليتها',
  'quality.ar.onTime': 'في وقته',
  'quality.ar.jamaah': 'جماعة',
  'quality.ar.takbirah': 'التكبيرة الأولى',

  'log.adhan': 'Adhān {{time}}',
  'log.save': 'Save',
  'log.saved': 'Saved',
  'log.makeUp.kids': 'I prayed it late — make-up',
  'log.makeUp.adult': 'Record as qaḍā’',
  'log.allFour.title': 'All four, mā shā’ Allāh',
  'log.allFour.sub': 'your {{prayer}} mark is complete',
  'log.saveFailed': 'This phone would not keep the record. Nothing has been lost — try again.',
  'log.privacy': 'This stays on your phone. Nobody else can see it.',
  'log.close': 'Close without recording',
  'log.onDay': 'Recording {{day}}',

  'settings.title': 'Settings',
  'settings.back': 'Back',
  'settings.whoIsPraying': 'Who is praying',
  'settings.name': 'Name',
  'settings.nameUnset': 'Not set',
  'settings.age': 'Age',
  'settings.lookAndFeel': 'Look and feel',
  'settings.byAge': 'Chosen by age — {{age}} gets the {{version}} version.',
  'settings.byHand': 'Changed by hand — age {{age}} would normally get the {{version}} version.',
  'settings.fun': 'Fun',
  'settings.funSub': 'Lanterns, colours, rewards',
  'settings.plain': 'Plain',
  'settings.backToAge': 'Back to the {{version}} version the age chooses',
  'settings.plainSub': 'Same record, no game layer',
  'settings.lightDark': 'Light and dark',
  'settings.day': 'Day',
  'settings.night': 'Night',
  'settings.auto': 'Auto',
  'settings.autoResolved': 'Auto · {{scheme}}',
  'settings.autoNote':
    'Auto turns to Day at Fajr and back to Night at Maghrib, off the same prayer times the streak uses.',
  'settings.themeColour': 'Theme colour',
  'settings.accentNote': 'the four prayer-quality colours never change',
  'settings.parentGate':
    'The fun version is for {{age}} and under. Anyone can change it here — deliberately, so a child is never locked out of how their own app looks.',
  'settings.language': 'Language',
  'settings.prayerTimes': 'Prayer times',
  'settings.sampleTimes': 'Showing sample times until you set a location.',
  'settings.useLocation': 'Use my location',
  'settings.updateLocation': 'Update location',
  'settings.locating': 'Finding you…',
  'settings.locationDenied': 'Location was not shared, so sample times stay.',
  'settings.locationSet': '{{lat}}, {{lon}}',
  'settings.method': 'Calculation method',
  'settings.methodNote': 'Communities differ on the twilight angles. Follow your local masjid.',
  'settings.madhab': '‘Asr timing',
  'settings.madhab.shafi': 'Shāfi‘ī',
  'settings.madhab.hanafi': 'Ḥanafī',

  'accent.brass': 'Brass',
  'accent.terracotta': 'Terracotta',
  'accent.rose': 'Rose',
  'accent.olive': 'Olive',
  'accent.lapis': 'Lapis',
  'accent.emerald': 'Emerald',

  'nav.label': 'Main',
  'nav.home': 'Home',
  'nav.report': 'Report',

  'report.previous': 'Earlier days',
  'report.next': 'Later days',
  'report.theseDays': 'These seven days',
  'report.recorded': '{{count}} / {{total}}',
  'report.recordedLabel': 'prayers recorded',
  'report.fullDays': 'days all five',
  'report.perfect': 'prayers with all four',
  'report.bestRun': 'best run of full days',
  'report.statistics': 'Statistics',
  'report.rangeLabel': 'How many days the statistics cover',
  'report.dayCount': { one: '{{count}} day', other: '{{count}} days' },
  'report.statsNote': 'The days up to today, whichever week the grid above is showing.',
  'report.statsTotal': { one: '{{count}} prayer in them.', other: '{{count}} prayers in them.' },
  'report.qualityShare': '{{quality}}: {{count}} of {{total}} prayers',
  'report.eachPrayer': 'Each prayer, each of the four',
  'report.eachPrayerNote':
    'One missed Fajr does not touch the other four. Every prayer’s own run for each of the four, in days.',
  'report.scaleWeek': 'All twenty bars share one scale: a full bar is a week.',
  'report.scaleDays':
    'All twenty bars share one scale: a full bar is {{count}} days, notched at a week and at a month.',
  'report.inARow': { one: '{{count}} day in a row', other: '{{count}} days in a row' },
  'report.longestRun': 'Longest run',
  'report.prayerQuality': '{{prayer}}, {{quality}}: {{run}}',
  'report.prayerQualityBest': '{{prayer}}, {{quality}}: {{run}}, longest ever {{best}}',
  'report.starNote': 'The star on a bar is the longest that run has ever been.',
  'report.empty': 'Nothing recorded in these days yet.',
  'report.legend':
    'Each mark shows the four qualities: prayed, in its time, in jamā‘ah, first takbīrah.',
  'report.editHint': 'Tap any prayer to record it, on any of these days.',
  'report.cellLabel': '{{prayer}}, {{day}}: {{note}}',
};
